// View-only staff, in the browser.
//
// THE SERVER IS THE CONTROL. squish-crm-backend refuses every state-changing
// request from a person with no staff role (lib/staff-access.js there), and
// nothing in this file can change that or needs to. This is the courtesy half:
// a viewer should not be shown buttons that can only fail, and a write this UI
// did not think to hide should be stopped with a sentence instead of a raw 403.
//
// Three pieces, all keyed on what GET /api/whoami said:
//   - isReadOnlyStaff(me): the one predicate;
//   - the `data-view-only` flag on <html>, which App.css uses to remove every
//     control marked `data-write`;
//   - a fetch backstop that answers a viewer's write locally, before it leaves.

// The writes a view-only person may send. Mirrors READ_ONLY_WRITE_ALLOWLIST in
// squish-crm-backend/server.js; if the two drift, the server's list wins and
// the worst outcome is a 403 this file could have explained more gently.
const VIEW_ONLY_MAY_SEND = new Set([
  'POST /api/otp/view-only',
  'POST /api/console/stream',
  'POST /api/console/command',
]);

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Is this the whoami of someone who may only read?
 *
 * `readOnly` is the server's own answer. `role === 'viewer'` covers a backend
 * that predates the field. Anything else — including whoami not having
 * resolved yet, or having failed — is NOT read-only here: hiding the controls
 * of a working staffer on a slow or failed whoami would be the worse mistake,
 * and the server refuses a real viewer regardless.
 */
export function isReadOnlyStaff(me) {
  return !!me && (me.readOnly === true || me.role === 'viewer');
}

const STAFF_ROLES = new Set(['admin', 'sales', 'support']);

/** Someone with a Salesforce seat and a mapped role: they can act. */
export function hasStaffRole(me) {
  return !!me && STAFF_ROLES.has(me.role);
}

/** A viewer: view-only is the ONLY way they open an account. */
export function isViewer(me) {
  return !!me && me.role === 'viewer';
}

/**
 * May this person open an account view-only — no customer code, read-only,
 * recorded? A viewer, and licensed staff too: the customer's code is consent
 * to ACT, not to look. A login with no role gets neither door.
 */
export function canOpenViewOnly(me) {
  return isViewer(me) || hasStaffRole(me);
}

/** A Salesforce seat whose profile maps to no CRM role: locked out, and fixable. */
export function hasUnmappedProfile(me) {
  return !!me && me.unmappedProfile === true;
}

/** The sidebar label for a role. */
export function roleLabel(role, me = null) {
  if (hasUnmappedProfile(me)) return 'Profile not mapped';
  return ({ admin: 'Admin', sales: 'Sales', support: 'Support', viewer: 'View only', unknown: 'Guest' })[role] || 'Guest';
}

/**
 * Would the server refuse this request from a view-only person?
 * Only /api/ calls are judged; anything else (a third-party script, an asset)
 * is none of this module's business.
 */
export function viewOnlyBlocks(method, url) {
  const verb = String(method || 'GET').toUpperCase();
  if (SAFE_METHODS.has(verb)) return false;
  let pathname;
  try {
    pathname = new URL(String(url), 'https://work.squish.co').pathname;
  } catch {
    return true;   // a write we cannot even parse is not one to wave through
  }
  if (!pathname.startsWith('/api/')) return false;
  return !VIEW_ONLY_MAY_SEND.has(`${verb} ${pathname}`);
}

// ─── Session state ───────────────────────────────────────────────────────────
// One whoami per load, held here so the fetch backstop (which is not a React
// component) and any component can ask the same question.

let current = null;
const listeners = new Set();

export function setStaff(me) {
  current = me || null;
  if (typeof document !== 'undefined') {
    if (isReadOnlyStaff(current)) document.documentElement.dataset.viewOnly = 'true';
    else delete document.documentElement.dataset.viewOnly;
  }
  for (const fn of listeners) {
    try { fn(current); } catch (e) { console.warn('[staffAccess] listener failed:', e); }
  }
}

export function currentStaff() { return current; }
export function isReadOnly() { return isReadOnlyStaff(current); }

export function subscribeStaff(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ─── The fetch backstop ──────────────────────────────────────────────────────
// Wraps window.fetch once. For a view-only person, a state-changing /api/ call
// that the server would refuse is answered here with the same 403 body the
// server sends, and `onBlocked` is told so the app can say so in words.

export const VIEW_ONLY_MESSAGE = 'Your access is view-only, so nothing was changed.';

function requestParts(input, init) {
  const isRequest = typeof Request !== 'undefined' && input instanceof Request;
  return {
    method: init?.method || (isRequest ? input.method : 'GET'),
    url: isRequest ? input.url : input,
  };
}

// `gate`, if given, may hold a request before it leaves (lib/accountIdle's
// "right account?" check): it returns null to send now, or a promise of null
// (send it) or of a Response (answer the caller with that instead). A view-only
// person's write is refused first: there is no point confirming a change the
// server will not take.
export function guardedFetch(realFetch, { onBlocked = null, onResponse = null, gate = null } = {}) {
  const send = (input, init, method, url) => {
    const sent = realFetch(input, init);
    if (typeof onResponse !== 'function') return sent;
    // An observer, never a participant: whatever it does or throws, the caller
    // gets the untouched response.
    return sent.then((res) => {
      try { Promise.resolve(onResponse(res, { method, url })).catch(() => {}); } catch { /* observer only */ }
      return res;
    });
  };
  return function viewOnlyFetch(input, init) {
    const { method, url } = requestParts(input, init);
    if (isReadOnly() && viewOnlyBlocks(method, url)) {
      if (typeof onBlocked === 'function') {
        try { onBlocked({ method: String(method).toUpperCase(), url: String(url) }); } catch { /* the notice is optional */ }
      }
      return Promise.resolve(new Response(
        JSON.stringify({ error: VIEW_ONLY_MESSAGE, errorCode: 'READ_ONLY_ROLE', role: current?.role || 'unknown' }),
        { status: 403, headers: { 'Content-Type': 'application/json' } },
      ));
    }
    let held = null;
    if (typeof gate === 'function') {
      try { held = gate({ method, url }); } catch { held = null; }
    }
    if (!held) return send(input, init, method, url);
    return Promise.resolve(held).then(answer => answer || send(input, init, method, url));
  };
}

let installed = false;
export function installViewOnlyFetchGuard({ onBlocked, onResponse, gate } = {}) {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  window.fetch = guardedFetch(window.fetch.bind(window), { onBlocked, onResponse, gate });
}
